// src/pages/HomePage.js
/**
 * Home dashboard shown **after a successful sign-in**.
 *
 * Purpose ──────────────────────────────────────────────────────────
 *  • Keeps today's due work front and center after sign-in.
 *  • Uses the top section as status-first guidance rather than a broad launcher.
 *
 * UX / visual layout ───────────────────────────────────────────────
 *  • First landing after onboarding introduces the To do list (see
 *    `utils/tasks/todoIntroPrompt`).
 *
 * @returns {JSX.Element}
 */

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics/track";
import ErrorPopup from "../components/ErrorPopup";
import RegionalSupportCodeNotice from "../components/backend/RegionalSupportCodeNotice";
import HomePageLoadingShell from "../components/pageLoading/HomePageLoadingShell";
import HomeActivityHistory from "../components/home/HomeActivityHistory";
import HomeEnvironmentalContextCard from "../components/home/HomeEnvironmentalContextCard";
import ProtocolTaskList from "../components/tasks/ProtocolTaskList";
import TaskListEmptyStateCard from "../components/tasks/TaskListEmptyStateCard";
import { usePresentationLabelling } from "../hooks/usePresentationLabelling";
import { getRegionalSupportCode } from "../utils/backend/regionalSupportCodes";
import {
  getTaskBoardCompletionSummary,
  getTaskBoardNextDueDate,
} from "../utils/tasks/taskBoardSummary";
import {
  clearTodoIntroPending,
  isTodoIntroPending,
} from "../utils/tasks/todoIntroPrompt";

function getHomeTaskBoardCompletionSummary(board) {
  const summary = getTaskBoardCompletionSummary(board);

  if (summary.total > 0) {
    return summary;
  }

  const tasks = Array.isArray(board?.tasks) ? board.tasks : [];
  const completed = tasks.filter((task) => task?.state === "completed").length;

  return {
    total: tasks.length,
    completed,
    outstanding: Math.max(tasks.length - completed, 0),
  };
}

function formatHomeNextDueDate(date, locale) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
  }).format(date);
}

function buildHomeTaskListHeader(board, t) {
  if (board?.loading || (board?.state && board.state !== "ready")) {
    return t("taskList.page.title");
  }

  const { total, outstanding } = getHomeTaskBoardCompletionSummary(board);

  if (total <= 0) {
    return t("taskList.page.title");
  }

  return t(
    outstanding === 1
      ? "home.taskListSummary.headingOne"
      : "home.taskListSummary.headingOther",
    {
      remaining: outstanding,
    },
  );
}

function HomePage({
  db,
  user,
  isPermitted,
  taskBoard,
  loadingModes,
  historyRefreshKey = 0,
  isClinicScoped = false,
  recordingAccessError = null,
}) {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const { appName } = usePresentationLabelling("userWebApp");
  const [showTodoIntro, setShowTodoIntro] = useState(false);
  const locale = i18n.resolvedLanguage || i18n.language || "en-GB";

  useEffect(() => {
    trackEvent("home_view");
  }, []);

  // Shown on the first Home landing after the user completes their first run of
  // the onboarding form. Reading the flag does not clear it, so a remount here
  // cannot swallow an introduction the user has not acknowledged yet.
  useEffect(() => {
    if (!user?.uid) return;
    setShowTodoIntro(isTodoIntroPending(user.uid));
  }, [user?.uid]);

  const dismissTodoIntro = useCallback(() => {
    clearTodoIntroPending(user?.uid);
    setShowTodoIntro(false);
  }, [user?.uid]);

  const todoIntroPopup = useMemo(() => {
    if (!showTodoIntro) return null;

    return {
      title: t("todoIntroPopup.title", {
        appName,
        defaultValue: `Welcome to ${appName}`,
      }),
      message: t("todoIntroPopup.body"),
      cancelLabel: t("close"),
      confirmLabel: t("navBar.todo", "To Do"),
      confirmIcon: "checklist",
      dialogLabel: t("todoIntroPopup.ariaLabel", {
        appName,
        defaultValue: `Welcome to ${appName}`,
      }),
      onConfirm: () => navigate("/todo"),
    };
  }, [appName, navigate, showTodoIntro, t]);
  const displayTaskBoard = useMemo(
    () => ({
      ...(taskBoard || {}),
      loading: loadingModes || taskBoard?.loading,
    }),
    [loadingModes, taskBoard],
  );
  const homeTaskBoard = useMemo(() => {
    const tasks = Array.isArray(displayTaskBoard?.tasks)
      ? displayTaskBoard.tasks.filter((task) => task?.state !== "completed")
      : [];

    return {
      ...(displayTaskBoard || {}),
      tasks,
    };
  }, [displayTaskBoard]);
  const showHomeTaskList =
    homeTaskBoard.loading ||
    (homeTaskBoard.state === "ready" && homeTaskBoard.tasks.length > 0);
  const homeTaskBoardSummary = useMemo(
    () => getHomeTaskBoardCompletionSummary(displayTaskBoard),
    [displayTaskBoard],
  );
  const showHomeCompletionCard =
    !loadingModes &&
    displayTaskBoard?.state === "ready" &&
    homeTaskBoardSummary.total > 0 &&
    homeTaskBoardSummary.outstanding === 0;
  const nextDueDateLabel = useMemo(
    () => formatHomeNextDueDate(getTaskBoardNextDueDate(displayTaskBoard), locale),
    [displayTaskBoard, locale],
  );
  const taskListHeader = useMemo(
    () => buildHomeTaskListHeader(displayTaskBoard, t),
    [displayTaskBoard, t],
  );

  return (
    <div className="w-full max-w-lg min-h-full flex flex-col items-center gap-4 ">
      <div className="flex flex-col items-center gap-4 px-4 md:px-0 w-full h-fit">
        <section className="w-full flex flex-col gap-2">
          {loadingModes ? (
            <HomePageLoadingShell modeOnly />
          ) : showHomeTaskList ? (
            <div className="w-full flex flex-col gap-2">
              <div className="flex items-start justify-between gap-3 px-1">
                <h2 className="subtitle-text">{taskListHeader}</h2>
                {!homeTaskBoard.loading ? (
                  <Link
                    to="/todo"
                    className="inline-flex shrink-0 items-center gap-0.5 pt-1 text-sm font-medium text-primary"
                  >
                    {t("home.history.viewAllTasks", "View all")}
                    <span
                      className="material-symbols-outlined text-[18px] leading-none"
                      aria-hidden="true"
                    >
                      chevron_right
                    </span>
                  </Link>
                ) : null}
              </div>
              <ProtocolTaskList
                board={homeTaskBoard}
                hideHeader
                className="!bg-transparent !shadow-none !rounded-none !p-0"
                tasksClassName="gap-1"
                groupedRows
                emphasizeCounts
              />
            </div>
          ) : showHomeCompletionCard ? (
            <TaskListEmptyStateCard
              icon="list_alt_check"
              title={t(
                "taskList.sections.due.completeTitle",
                "You're all done for today",
              )}
              message={
                nextDueDateLabel
                  ? t("taskList.sections.due.completeNextDue", {
                      date: nextDueDateLabel,
                      defaultValue: "Your next tasks are due on {{date}}.",
                    })
                  : null
              }
              grouped
              iconClassName="text-primary"
              titleClassName="text-typography font-semibold leading-tight"
              textClassName="text-typography leading-tight"
            />
          ) : null}
        </section>

        {!loadingModes && isClinicScoped && recordingAccessError ? (
          <section className="w-full rounded-2xl border border-greyscale-6 bg-greyscale-8/40 p-4 text-left">
            <p className="subtitle-text mb-2">
              {t("regionalBackend.studyAccess.title")}
            </p>
            <p className="muted-text">
              {recordingAccessError === "fetch_failed"
                ? t("regionalBackend.studyAccess.fetchFailed")
                : t("regionalBackend.studyAccess.notFound")}
            </p>
            <RegionalSupportCodeNotice
              supportCode={getRegionalSupportCode(
                recordingAccessError === "fetch_failed"
                  ? "STUDY_ACCESS_FETCH_FAILED"
                  : "STUDY_ACCESS_NOT_FOUND",
              )}
            />
          </section>
        ) : null}
      </div>
      <HomeActivityHistory
        db={db}
        uid={user?.uid}
        isPermitted={isPermitted}
        loadingModes={loadingModes}
        historyRefreshKey={historyRefreshKey}
        taskBoard={displayTaskBoard}
        showCompletedTaskCelebration={!showHomeCompletionCard}
        todaySupplement={
          <HomeEnvironmentalContextCard refreshKey={historyRefreshKey} />
        }
      />

      <ErrorPopup popup={todoIntroPopup} onClose={dismissTodoIntro} />
    </div>
  );
}

export default memo(HomePage);
